import { Box, Button, Dialog, DialogContent, Fab, FormControlLabel, IconButton, Slider, Stack, Typography } from "@mui/material";
import mapboxgl, { LngLatLike, Map, Marker } from "mapbox-gl";
import React, { useCallback, useEffect, useRef, useState } from "react";
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import {
  calculateRotatedCoordinates,
  calculateScaledCoordinates,
  loadOverlay,
  loadOverlayByCoordinate,
  useRenderGISElementOnMap,
  useRenderLinesOnMap,
  useRenderPointOnMap,
  useRenderRobotOnMap,
} from "./helpers/map";
import OverlaySetting from "./models/OverlaySetting";
import CloseIcon from '@mui/icons-material/Close';
import "./RobotMap.css";
import 'mapbox-gl/dist/mapbox-gl.css';
import { mapboxToken, OVERLAY_APPEND_KEY } from "./constants";
import { Robot } from "./models/Robot";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentFloor, setDefaultFloor, setIconType, setZoomLevel } from "../../store/map/mapSlice";  // import the action creator
import { KeyboardArrowUp, NavigateBefore, NavigateNext, NorthEastRounded, RotateLeft, RotateRight, SouthWestRounded, VisibilityOffRounded, VisibilityRounded } from "@mui/icons-material";
import { useListMap } from "../../hooks/map";
import { bbox, bboxPolygon, center, centroid, points } from "@turf/turf";
import { MapType } from "../../hooks/map.type";
import { useLocationPoints } from "../../hooks/location";
import { smoothTransition } from "../../utils/animate";
import { triggerFire } from "../../store/fire/fireTriggerSlice";
import { border, shadow } from "../../utils/styles";
import LiftIcon from "./_icons/lift";
import DoorIcon from "./_icons/door";
import { Switch } from "../Switch";
import PinPointIcon from "./_icons/pinpoint";
import { customLog, formatDate, isValidCoordinate, toDegree } from "../../utils/utils";
import { VIEW } from "../../utils/breakpoint";
import { Coordinates } from "./RobotMap.type";
import { mockURL } from "../../utils/mock";
import { mockRobotMaps } from "./mock-robot-map";
import { useRender3DRobotOnMap } from "./helpers/3d-marker-renderer";
import { IOSSwitch } from "../Switch/ios-switch";
import { CLEANING_DIALOG_QUERY_PARAM, CLEANING_MODE_ENABLED, HIDE_DASHBOARD_CARD_KEY, show3DFeature, SHOW_ZONE_QUERY_PARAM, showRobotMapOnSelected, TASK_MODE_QUERY_PARAM } from "../../utils/const";
import { useFireAlarmContext } from "../../context/FireAlarmContext";
import { dismissFireAlarm, fireNotificationTemplate, showFireAlarm, useDismissAlarm } from "../../hooks/system_event";
import { criticalColor } from "../../routes/dashboard/components/Notification";
import FireAlarmIcon from "../icons/FireAlarmIcon";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useRenderZoneOnMap } from "./helpers/cleaning-map-helper";
import { DashboardMode } from "../../utils/enums/Dashboard";
import { useDashboardMode } from "../../hooks/appMode";
import { addRippleLayer } from "../../routes/dashboard/components/cleaning/ZoneSelectedDialog";
import { useExpandSelectedZone, useExpandUnselectedZone } from "../../hooks/cleaning/state";
import { useLocalStorage } from "usehooks-ts";

mapboxgl.accessToken = mapboxToken;

export interface RobotMapProps {
  startLocation: LngLatLike;
  isFallbackUsed?: boolean;
  zoomLevel: number;
  robots: Robot[];
  children?: React.ReactElement<GIS>[];
  line?: [number, number][];
  onRobotClick?: (robot: Robot) => void;
  renderLocationMarkers?: () => JSX.Element[];
  mapHeight?: number
}

export type MapLevelType = { level: number, building: string, name: string }

export interface GIS {
  GIS: LngLatLike;
}

type LoadedMap = {
  overlaySetting: OverlaySetting,
  name: string
}

type SenderRecipientType = {
  id: string,
  location_name: string,
  map: {
    map_name: string,
    map: string,
    building: string,
    robot_map_name: string,
    map_level: number
  }[],
  pose: {
    latitude: number,
    longitude: number,
    z: number,
    theta: number
  },
  store_manager_id: string[]
}

const moreControlButtonStyle = { flex: 'none', minWidth: '36px' }

const RobotMap: React.FC<RobotMapProps> = ({
  startLocation,
  isFallbackUsed,
  zoomLevel,
  robots,
  children = [],
  line = [],
  onRobotClick,
  mapHeight,
  renderLocationMarkers
}: RobotMapProps) => {
  const mapContainer = useRef<HTMLElement | string>("");
  const map = useRef<Map | null>(null);
  const dispatch = useDispatch();

  const isFirstLoadRef = useRef(true)

  const [showLift, setShowLift] = useState(false);
  const [showDoor, setShowDoor] = useState(false);
  const { data: doorPointData } = useLocationPoints("door_point", { enabled: showDoor });
  const { data: liftPointData } = useLocationPoints("lift_point", { enabled: showLift });

  const doorPoints = doorPointData ? doorPointData.data : [];
  const liftPoints = liftPointData ? liftPointData.data : [];

  const [showExtendMenu, setShowExtendMenu] = useState(false)
  const [showDeliveryPoint, setShowDeliveryPoint] = useState(true)

  const [showZone,] = useQueryState(SHOW_ZONE_QUERY_PARAM, parseAsBoolean.withDefault(false).withOptions({ clearOnDefault: true }));
  const { dashboardMode, isCleaningMode } = useDashboardMode();

  const [selectedMapName, setSelectedMapName] = useState("");
  const [showControlMenu, setShowControlMenu] = useState(false)
  const [loadedMaps, setLoadedMaps] = useState<LoadedMap[]>([])
  const [selectedOpacity, setSelectedOpacity] = useState(100);
  const [lastZoom, setLastZoom] = useState<number>(18);
  const [lastLngLat, setLastLngLat] = useState<mapboxgl.LngLat>();
  const [lastFloorLevel, setLasFloorLevel] = useState('');
  const rotateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const opacityIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isHideCard,] = useLocalStorage(HIDE_DASHBOARD_CARD_KEY, false)
  const { data: dataMaps, isLoading: isLoadingMap } = useListMap()

  const { setIsExpanded } = useExpandSelectedZone()
  const { setIsExpandedUnselected } = useExpandUnselectedZone()

  const currentFloor = useSelector((state: any) => state.map.currentFloor);

  const [robotsLngLats, setRobotsLngLats] = useState<{
    id: string,
    lngLat: [number, number] | LngLatLike
  }[]>([]);
  const [currentLngPointerIndex, setCurrentLngPointerIndex] = useState(0)

  const onSelectMap = ({ overlaySetting, name }: {
    overlaySetting: OverlaySetting,
    name: string
  }) => {
    setSelectedMapName(name)
    if (!map.current) {
      return
    }
    try {
      // Show Overlay of selected Map (TMP DISABLED)
      // map.current.setPaintProperty(name + OVERLAY_APPEND_KEY, "fill-opacity", 0.15)
      setSelectedOpacity(overlaySetting.opacity)
    } catch (error) { }
  }

  const onDeselect = () => {
    setSelectedMapName('')
    setShowControlMenu(false)
    // TMP DISABLED MAP CONTROL -> hide all overlay fill
    // loadedMaps.map((item) => {
    //   map.current?.setPaintProperty(item.name + OVERLAY_APPEND_KEY, "fill-opacity", 0)
    // })
  }

  // Get Last Coordinate before Open Delivery and restore when closed
  const isDeliveryBoardOpen = useSelector((state: any) => state.deliveryDashboard.isOpen);
  useEffect(() => {
    if (!map.current) return
    if (isDeliveryBoardOpen) {
      setLastZoom(map.current.getZoom())
      setLastLngLat(map.current.getCenter())
      setLasFloorLevel(currentFloor)
      return
    }
    if (!isDeliveryBoardOpen) {
      if (lastFloorLevel !== '') {
        dispatch(setCurrentFloor(lastFloorLevel))
      }
      map.current.flyTo({
        center: lastLngLat,
        zoom: lastZoom
      })
    }
  }, [isDeliveryBoardOpen])

  const updateLayoutPropertyWithOverlay = (name: string, propertyName: "visibility", value: any) => {
    if (!map.current) {
      return
    }
    map.current?.setLayoutProperty(name, propertyName, value);
    // map.current?.setLayoutProperty(name + OVERLAY_APPEND_KEY, propertyName, value);
  }

  const addMap = (overlaySetting: OverlaySetting, name: string, loadByDirectCoordinate = false, hideVisibility = false) => {
    if (!map.current) {
      return
    }

    // Stop when map name is already exist in array
    const find = loadedMaps.find((item) => item.name === name);
    if (!!find) {
      return
    }
    const tmpMaps = loadedMaps;
    tmpMaps.push({ overlaySetting, name });
    if (loadByDirectCoordinate) {
      loadOverlayByCoordinate(map, overlaySetting, name);
    } else {
      loadOverlay(map, overlaySetting, name);
    }
    setLoadedMaps(tmpMaps)

    // If already more than 1 map, then set default visibility as 'hidden'
    if (tmpMaps.length > 1 || hideVisibility) {
      updateLayoutPropertyWithOverlay(name, "visibility", "none")
    }
  }

  const filterMapOnly = (data: MapType[]) => {
    if (!data || data.length === 0) {
      return []
    }

    return data.filter((item) => !!item.map_image && item.robot === '' && item.map_longitude_tl > 0 && item.map_latitude_br > 0)

  }

  useEffect(() => {
    if (!startLocation) {
      return
    }
    if (map.current) return; // initialize map only once
    if (!mapContainer.current) return
    customLog("reinitializing map");
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/hanscau/clxan4269025p01qxaxqc0ekr",
      // center: [103.83484878754473, 1.2782008843142307],
      center: startLocation,
      zoom: zoomLevel,
      minZoom: 15,
      maxZoom: 26,
      pitch: 0,
      bearing: 0,
      antialias: true,
      interactive: true
    });

    // Remove Label
    map.current.on("load", () => {
      if (map.current && map.current.isStyleLoaded()) {
        const layers = map.current.getStyle().layers as mapboxgl.Layer[];
        layers.forEach(layer => {
          // remove text label
          if (layer.type === "symbol") {
            map.current?.removeLayer(layer.id);
          }
        });

        addRippleLayer(map.current);
      }

      map.current?.on('click', (e) => {
        console.log("coord", e.lngLat);

      });


      setTimeout(() => {
        map.current?.on('click', (e) => {
          // customLog("coord", e.lngLat);

          if (e._defaultPrevented === false) {
            setIsExpanded(false)
            setIsExpandedUnselected(false)
          }
        });
      }, 12000); //TODO: Need Better Solution
    })

    // });
    // map.current.addControl(new mapboxgl.NavigationControl());
  }, [startLocation]);

  const setMapVisibility = () => {
    if (!map.current) return
    // map.current.setLayoutProperty('map', 'visibility', 'hidden');

    loadedMaps.forEach((map) => {
      updateLayoutPropertyWithOverlay(map.name, "visibility", currentFloor === map.overlaySetting.level?.toString() ? "visible" : "none")
    });
  }

  const initMap = (listMaps: MapType[]) => {
    if (!map.current || !map.current.getStyle()) {
      return
    }

    filterMapOnly(listMaps).map((map) => {
      const lng_tl = map?.map_longitude_tl || 0
      const lat_tl = map?.map_latitude_tl || 0
      const lng_br = map?.map_longitude_br || 0
      const lat_br = map?.map_latitude_br || 0

      const coordinates = [
        [lng_tl, lat_tl],
        [lng_br, lat_tl],
        [lng_br, lat_br],
        [lng_tl, lat_br]
      ]

      const degreeRotation = toDegree(map?.map_orientation || 0)
      const rotatedCoordinates = calculateRotatedCoordinates(coordinates, degreeRotation);

      const mapOverlayData: OverlaySetting = {
        uuid: map.map_uuid,
        url: mockURL(map.map_image),
        topLeftLng: map.map_longitude_tl,
        topLeftLat: map.map_latitude_tl,
        widthInGIS: Math.abs(map.map_longitude_br - map.map_longitude_tl),
        heightInGIS: Math.abs(map.map_latitude_tl - map.map_latitude_br),
        opacity: 100,
        level: map.map_level,
        rotation: toDegree(map.map_orientation || 0),
        coordinates: rotatedCoordinates
      };

      addMap(mapOverlayData, map.map_name_display.split(" ").join("_"));
    });

    setMapVisibility()

    // Add Mock Robot Map only when showRobotMapOnSelected is true
    showRobotMapOnSelected && mockRobotMaps.map((map) => {
      const mapOverlayData: OverlaySetting = {
        coordinates: map.coordinates,
        uuid: map.uuid,
        url: mockURL(map.url),
        topLeftLng: map.coordinates[0][0],
        topLeftLat: map.coordinates[0][1],
        widthInGIS: Math.abs(map.coordinates[1][0] - map.coordinates[0][0]),
        heightInGIS: Math.abs(map.coordinates[0][1] - map.coordinates[2][1]),
        opacity: 75,
        level: map.level,
        rotation: map.rotation || 0,
      };

      addMap(mapOverlayData, map.uuid.split(" ").join("_"), true, true);
    })
  }

  useEffect(() => {
    if (!map.current || loadedMaps.length > 0 || !dataMaps) {
      return
    }

    const validateInit = () => {
      if (!map.current) {
        return
      }
      const listMaps = dataMaps?.data || [];
      if (loadedMaps.length === 0 && listMaps.length > 0) {
        const handleStyleLoad = () => {
          initMap(listMaps);
        }

        if (map.current.isStyleLoaded()) {
          handleStyleLoad();
          return
        }
        map.current.off('style.load', handleStyleLoad)
        map.current.on('style.load', handleStyleLoad)
      }
    }

    const timeOut = setTimeout(validateInit, 3000);
    return () => clearTimeout(timeOut);
  }, [loadedMaps, map.current, dataMaps]);



  // This to call init Map in case map style is loaded but the loaded map still empty
  useEffect(() => {
    if (!map.current || loadedMaps.length > 0 || !dataMaps) {
      return
    }

    const listMaps = dataMaps?.data || [];

    if (listMaps.length > 0 && !!map.current && loadedMaps.length === 0 && map.current.isStyleLoaded()) {
      // setLoadedMaps([]);
      initMap(listMaps);
    }
  }, [dataMaps, map.current?.isStyleLoaded(), loadedMaps.length]);

  useEffect(() => {
    if (isLoadingMap || !dataMaps || !map.current) {
      return
    }

    const { data: listMaps } = dataMaps
    if (!listMaps || listMaps.length === 0) {
      return
    }


    const handleStyleLoad = () => {
      setLoadedMaps([]);
      initMap(listMaps);
    }

    if (map.current.isStyleLoaded()) {
      handleStyleLoad();
      return
    }
    map.current.off('style.load', handleStyleLoad)
    map.current.on('style.load', handleStyleLoad)

    return () => {
      if (map.current) {
        map.current.off('style.load', handleStyleLoad)
      }
    }

    // return () => map.current?.remove();
  }, [map.current, isLoadingMap, dataMaps])

  const robotRef = useRef<Robot[]>(robots);
  robotRef.current = robots
  // call useRenderGISElementOnMap on mount
  const handleOnRobotClick = (robotData: Robot) => {

    // const findCurrentRobot = robots.find((r) => r.robotId === robotData.robotId);
    if (robotRef.current.length === 0) {
      return
    }

    const findCurrentRobot = robotRef.current.find((r) => r.robotId === robotData.robotId);
    if (!findCurrentRobot) {
      return
    }
    onRobotClick?.(findCurrentRobot)
  };

  const onRobotClickRef = useRef(handleOnRobotClick);
  onRobotClickRef.current = handleOnRobotClick;

  useRenderGISElementOnMap(map, children, showDeliveryPoint && !isCleaningMode);
  // useMockRobotMovements(robots);
  useRenderRobotOnMap(map, robots, onRobotClickRef.current);

  // useRender3DRobotOnMap(map, robots, onRobotClick);
  useRenderLinesOnMap(map, line);
  useRenderPointOnMap(map, doorPoints, "door_point", showDoor)
  useRenderPointOnMap(map, liftPoints, "lift_point", showLift)

  useRenderZoneOnMap(map, children, showZone);

  // renderLocationMarkers && renderLocationMarkers();

  customLog("rendering map", robots);
  const updateZoomLevel = useSelector((state: any) => state.map.zoomLevel);
  const selectedRobot = useSelector((state: any) => state.robot.selectedRobot);
  const isSelectedRobot = useSelector((state: any) => state.robot.isSelectedRobot);
  const isJobSelected = useSelector((state: any) => state.job.isJobSelected);
  const selectedJob = useSelector((state: any) => state.job.selectedJob);
  const listOfPoints = useSelector((state: any) => state.map.mapDetails);
  const senderLocation = useSelector((state: any) => state.map.senderLocation);
  const recipientLocation = useSelector((state: any) => state.map.recipientLocation);
  const [isShowFireAlarmDialog, setIsShowFireAlarmDialog] = useState(false);

  const triggerFireState = useSelector((state: any) => state.fire.fireTrigger);
  // focus on location
  useEffect(() => {
    if (senderLocation != "" && Array.isArray(listOfPoints)) {
      const sender: SenderRecipientType = listOfPoints.find((point: any) => point.location_name === senderLocation);
      customLog("flying to sender", sender.pose);

      const isDifferentLevel = parseInt(currentFloor) !== sender.map[0].map_level
      if (isDifferentLevel) {
        dispatch(setCurrentFloor(sender.map[0].map_level.toString()))
        // Because not running when change floor level so, duplicate the code
        // TODO: recode the flow of floor level variable
        // Hide Every map first
        loadedMaps.map((item) => {
          updateLayoutPropertyWithOverlay(item.name, "visibility", "none")
        })

        const filter = loadedMaps.filter((item) => item.overlaySetting.level === parseInt(sender.map[0].map_level.toString()));

        filter.map(async (data) => {
          updateLayoutPropertyWithOverlay(data.name, "visibility", "visible")
        })

        const firstFindedMap = filter[0];
        if (!firstFindedMap || !firstFindedMap.overlaySetting.coordinates) {
          return
        }
      }

      map.current?.flyTo({ center: [sender.pose.longitude, sender.pose.latitude], zoom: 20 });
    }
  }, [senderLocation]);

  useEffect(() => {
    if (recipientLocation != "" && Array.isArray(listOfPoints)) {
      const recipient: SenderRecipientType = listOfPoints.find((point: any) => point.location_name === recipientLocation);
      const isDifferentLevel = parseInt(currentFloor) !== recipient.map[0].map_level
      if (isDifferentLevel) {
        dispatch(setCurrentFloor(recipient.map[0].map_level.toString()))
        // Because not running when change floor level so, duplicate the code
        // TODO: recode the flow of floor level variable
        // Hide Every map first
        loadedMaps.map((item) => {
          updateLayoutPropertyWithOverlay(item.name, "visibility", "none")
        })

        const filter = loadedMaps.filter((item) => item.overlaySetting.level === parseInt(recipient.map[0].map_level.toString()));

        filter.map(async (data) => {
          updateLayoutPropertyWithOverlay(data.name, "visibility", "visible")
        })

        const firstFindedMap = filter[0];
        if (!firstFindedMap || !firstFindedMap.overlaySetting.coordinates) {
          return
        }
      }

      map.current?.flyTo({ center: [recipient.pose.longitude, recipient.pose.latitude], zoom: 20 });
    }
  }, [recipientLocation]);

  const isShowRobotMap = useSelector((state: any) => state.map.showRobotMap);

  const handleShowRobotMap = () => {
    // Feature Flag Validation
    if (!showRobotMapOnSelected) {
      return
    }
    //Hide all mock robot maps
    loadedMaps.map((item) => {
      if (item.name.includes('MOCK-RM-')) {
        updateLayoutPropertyWithOverlay(item.name, "visibility", "none")
      } else {
        map.current?.setPaintProperty(item.name, 'raster-opacity', 1)
      }
    });

    if (!selectedRobot || !isShowRobotMap) {
      return
    }

    const robotMap = loadedMaps.find((item) => item.name === 'MOCK-RM-' + selectedRobot.robotStatus?.map_uuid);
    if (!robotMap) {
      return
    }

    updateLayoutPropertyWithOverlay(robotMap.name, "visibility", "visible");

    const layoutMap = loadedMaps.find((item) => item.overlaySetting.uuid === selectedRobot.robotStatus?.layout_uuid);
    if (!layoutMap) {
      return
    }

    map.current?.setPaintProperty(layoutMap.name, 'raster-opacity', 0.6)
  }

  // Update Robot Map Visibility when selectedRobot changes
  useEffect(() => {
    handleShowRobotMap()
  }, [selectedRobot, isShowRobotMap]);


  useEffect(() => {
    if (!robots || robots.length === 0 || !map.current) {
      return
    }

    changeRobotVisibility()
  }, [robots])

  useEffect(() => {
    customLog("flying to selected robot", selectedRobot.robotStatus?.pose);
    changeRobotVisibility()
    if (isSelectedRobot) {
      if (!isValidCoordinate(selectedRobot.robotStatus?.pose.longitude, selectedRobot.robotStatus?.pose.latitude)) {
        return
      }

      if (map.current) {
        map.current.flyTo({ center: [selectedRobot.robotStatus?.pose.longitude, selectedRobot.robotStatus?.pose.latitude], zoom: 20 });
      }
    }
    else {
      if (map.current) {
        map.current.flyTo({ center: startLocation, zoom: 18 });
      }
    }
  }, [isSelectedRobot]);

  useEffect(() => {
    if (isJobSelected && Array.isArray(listOfPoints)) {
      // get sender location
      customLog("flying to selected job", selectedJob);
      var senderLocation = listOfPoints.find((point: any) => point.location_name === selectedJob.sender_location);
      var recipientLocation = listOfPoints.find((point: any) => point.location_name === selectedJob.recipient_location);
      customLog("flying to selected job 1", senderLocation, recipientLocation);
      if (senderLocation === undefined || recipientLocation === undefined) {
        senderLocation = {
          pose: {
            longitude: 103.8585709834108,
            latitude: 1.282174630782123
          }
        }
        recipientLocation = {
          pose: {
            longitude: 103.8585709834108,
            latitude: 1.282174630782123
          }
        }
      }
      // find midpoint
      const midpoint = {
        longitude: (senderLocation.pose.longitude + recipientLocation.pose.longitude) / 2,
        latitude: (senderLocation.pose.latitude + recipientLocation.pose.latitude) / 2
      }
      customLog("flying to selected job 2", midpoint);
      if (!isValidCoordinate(senderLocation.pose.longitude, senderLocation.pose.latitude)) {
        return
      }
      if (!isValidCoordinate(recipientLocation.pose.longitude, recipientLocation.pose.latitude)) {
        return
      }
      if (!isValidCoordinate(midpoint.longitude, midpoint.latitude)) {
        return
      }
      if (map.current) {
        map.current.flyTo({ center: [midpoint.longitude, midpoint.latitude], zoom: 19 });
      }
    }
    else {
      if (map.current) {
        customLog("flying to selected job back");
        if (isSelectedRobot) {
          if (!isValidCoordinate(selectedRobot.robotStatus?.pose.longitude, selectedRobot.robotStatus?.pose.latitude)) {
            return
          }
          map.current.flyTo({ center: [selectedRobot.robotStatus?.pose.longitude, selectedRobot.robotStatus?.pose.latitude], zoom: 20 });
        } else {
          map.current.flyTo({ center: startLocation, zoom: 18 });
        }
      }
    }
  }
    , [selectedJob, isJobSelected]);

  useEffect(() => {
    customLog("updateZoomLevel", updateZoomLevel);
    if (map.current) {
      if (map.current.isStyleLoaded()) {
        map.current.flyTo({ zoom: updateZoomLevel });
      }
    }
  }, [updateZoomLevel]);

  useEffect(() => {
    if (map.current) {
      map.current.on('zoomend', () => {
        const newZoomLevel = map.current!.getZoom();
        dispatch(setZoomLevel(newZoomLevel));
        customLog('Current zoom level:', newZoomLevel);
      });
    }
  }, [map.current]);

  // Add state to track pending floor changes
  const pendingFloorChangeRef = useRef<string | null>(null);
  const pendingFloorChange = pendingFloorChangeRef.current;
  const [isProcessingFloorChange, setIsProcessingFloorChange] = useState(false);

  const processFloorChange = async () => {
    if (!map.current || isProcessingFloorChange) {
      return;
    }

    if (loadedMaps.length === 0) {
      pendingFloorChangeRef.current = currentFloor;
      return
    }

    setIsProcessingFloorChange(true)
    const floor = currentFloor;

    try {
      // Hide Every map first
      loadedMaps.map((item) => {
        updateLayoutPropertyWithOverlay(item.name, "visibility", "none")
      })
      const filter = loadedMaps.filter((item) => item.overlaySetting.level === parseInt(floor) && !item.name.includes('MOCK-RM'));

      filter.map(async (data) => {
        updateLayoutPropertyWithOverlay(data.name, "visibility", "visible")
      })

      const firstFindedMap = filter[0];
      if (!firstFindedMap || !firstFindedMap.overlaySetting.coordinates) {
        return
      }

      const coordinates = firstFindedMap.overlaySetting.coordinates
      // find bBox of selected Map Coordinates and return it as coordinates
      const bBoxCoords = bboxPolygon(bbox(points(coordinates))).geometry.coordinates.slice(0, 4)[0] as number[][]
      if (isFirstLoadRef.current && !isFallbackUsed) {
        setTimeout(() => {
          isFirstLoadRef.current = false;
        }, 300);
        if (!!firstFindedMap.overlaySetting.level) {
          dispatch(setDefaultFloor(firstFindedMap.overlaySetting.level.toString()));
        }
      } else {
        isFirstLoadRef.current = false;

        map.current.fitBounds([
          [bBoxCoords[4][0], bBoxCoords[4][1]],
          [bBoxCoords[2][0], bBoxCoords[2][1]]
        ], {
          // padding: 10,
          // zoom: 17.5,

        });
      }
      onDeselect();
      changeRobotVisibility()
    } catch (error) {
      console.error("Error processing floor change:", error);
    } finally {
      setIsProcessingFloorChange(false);
      handleShowRobotMap()
    }
    // }, [loadedMaps, isFirstLoadRef.current, isFallbackUsed, dataMaps, robots])
  }

  // Change Map Level
  useEffect(() => {
    // Wait for Loaded map to handle

    if (loadedMaps.length === 0) {
      pendingFloorChangeRef.current = currentFloor;
      return
    }

    processFloorChange()
  }, [currentFloor]);

  useEffect(() => {
    if (loadedMaps.length > 0 && pendingFloorChange !== null && !isProcessingFloorChange) {
      processFloorChange();
      pendingFloorChangeRef.current = null;
    }
  }, [loadedMaps, pendingFloorChange, isProcessingFloorChange]);

  // ONLY WORK IF 1 Level = 1 Map
  const changeRobotVisibility = () => {
    if (isLoadingMap || !dataMaps) {
      return
    }

    const selectedFloor = currentFloor

    // Get Selected Map Data
    const { data: listMaps } = dataMaps
    if (!listMaps || listMaps.length === 0) {
      return
    }

    const findMap = filterMapOnly(listMaps).find((item) => item.map_level === parseInt(selectedFloor))
    if (!findMap) {
      return
    }

    // Select All Robot Marker
    const allRobotElems = document.querySelectorAll(".robot-marker");
    if (!allRobotElems || allRobotElems.length === 0) {
      return
    }

    allRobotElems.forEach(element => {
      // Get Robot Id
      const robotIdFromElem = element.getAttribute("robotId");
      // Find from robots
      const robot = robots.find((item) => item.robotId === robotIdFromElem);
      if (!robot) {
        return
      }
      // Check if its Map uuid
      const isSameMapId = (robot.map_uuid && robot.map_uuid == findMap.map_uuid) || false as boolean;
      // check if robot layoutId = MapId
      const isMapToLayout = (robot.layout_uuid && robot.layout_uuid == findMap.map_uuid) || false as boolean;

      // Set Style (Show/Hide)
      robot.setVisibility(true);
      (element as any).style.display = (isSameMapId || isMapToLayout) ? "block" : "none"
    });
  }

  const setCoordinates = (name: string, coordinates: number[][]) => {
    (map.current?.getSource(name) as mapboxgl.ImageSource).setCoordinates(coordinates as Coordinates);
    // TMP DISABLED
    // (map.current?.getSource(name + OVERLAY_APPEND_KEY) as mapboxgl.GeoJSONSource).setData({
    //   type: "Feature",
    //   properties: {},
    //   geometry: {
    //     type: 'Polygon',
    //     coordinates: [coordinates],
    //   },
    // })
  }

  const onRotate = (degree: number) => {
    // If no map selected then stop
    if (!selectedMapName || !map.current) {
      return
    }
    // Find selectedMapName in loadedMaps Array
    const findMaps = loadedMaps.find((item) => item.name === selectedMapName);
    if (!findMaps || !findMaps.overlaySetting || !findMaps.overlaySetting.coordinates) return

    const currentRotation = (findMaps.overlaySetting.rotation ? findMaps.overlaySetting.rotation : degree)
    findMaps.overlaySetting.rotation = currentRotation + degree

    const newCoords = calculateRotatedCoordinates(findMaps.overlaySetting.coordinates, degree);
    findMaps.overlaySetting.coordinates = newCoords;
    setCoordinates(selectedMapName, newCoords)
  }

  const onChangeScale = (scaleFactor: number) => {
    if (!selectedMapName || !map.current) {
      return
    }
    const findMaps = loadedMaps.find((item) => item.name === selectedMapName);
    if (!findMaps || !findMaps.overlaySetting || !findMaps.overlaySetting.coordinates) return

    const newCoords = calculateScaledCoordinates(findMaps.overlaySetting.coordinates, scaleFactor);

    findMaps.overlaySetting.coordinates = newCoords;
    setCoordinates(selectedMapName, newCoords as Coordinates)
  };

  const onRotateLeft = () => {
    onRotate(-1)
  }

  const onRotateRight = () => {
    onRotate(1)
  }

  const rotateLeftHandler = () => {
    rotateIntervalRef.current = setInterval(() => {
      onRotateLeft()
    }, 200);
  }

  const removeRotateLeftHandler = () => {
    if (rotateIntervalRef.current) {
      clearInterval(rotateIntervalRef.current)
      rotateIntervalRef.current = null
    }
  }

  const rotateRightHandler = () => {
    rotateIntervalRef.current = setInterval(() => {
      onRotateRight()
    }, 200);
  }

  const removeRotateRightHandler = () => {
    if (rotateIntervalRef.current) {
      clearInterval(rotateIntervalRef.current)
      rotateIntervalRef.current = null
    }
  }

  const onChangeOpacity = (value: number) => {
    if (!selectedMapName || !map.current || value > 100 || value < 0) {
      return
    }

    const find = loadedMaps.find((item) => item.name === selectedMapName);

    if (!find) { return }
    find.overlaySetting.opacity = value || 0;
    setSelectedOpacity(value)
    map.current.setPaintProperty(selectedMapName, 'raster-opacity', value / 100)

  }

  const increaseOpacityHandler = () => {
    opacityIntervalRef.current = setInterval(() => {
      onChangeOpacity(selectedOpacity + 1)
    }, 200)
  }

  const decreaseOpacityHandler = () => {
    opacityIntervalRef.current = setInterval(() => {
      onChangeOpacity(selectedOpacity - 1)
    }, 200)
  }

  const removeOpacityHandler = () => {
    if (opacityIntervalRef.current) {
      clearInterval(opacityIntervalRef.current)
      opacityIntervalRef.current = null
    }
  }

  const getMapLevels = () => {
    if (!dataMaps || isLoadingMap || !dataMaps.data) {
      return [] as MapLevelType[]
    }

    const results: MapLevelType[] = []
    const notRobotMaps = filterMapOnly(dataMaps.data || []);

    notRobotMaps.map((item) => {
      const find = results.find((result) => result.level === item.map_level)
      if (!find) {
        results.push(({
          level: item.map_level as number || 0,
          building: item.building,
          name: item.map_level < 0 ? "B" + Math.abs(parseInt(item.map_level.toString())) : (item.map_level === 0 ? "GL" : "L" + item.map_level)
        }))
      }
    })

    return results.sort((a, b) => a.level > b.level ? -1 : (b.level > a.level ? 1 : 0))
  }

  const isShowAll = showDoor && showLift && showDeliveryPoint
  const setShowAll = (show: boolean) => {
    setShowDoor(show)
    setShowLift(show)
    setShowDeliveryPoint(show)
  }

  const iconType = useSelector((state: any) => state.map.iconType);
  const onIconTypeChange = (value: "2D" | "3D") => {
    if (!map.current) {
      return;
    }
    dispatch(setIconType(value));
  };

  const { isFireAlarmActive, deactivateFireAlarm } = useFireAlarmContext()

  const handleFireAlarm = () => {
    if (isFireAlarmActive) {
      // showFireAlarm(dispatch)
      setIsShowFireAlarmDialog(true)
    } else {
      dispatch(triggerFire())
    }
  }

  const { trigger: submitDismissAlarm } = useDismissAlarm({
    onSuccess: () => {
      setIsShowFireAlarmDialog(false)
      deactivateFireAlarm();
      dismissFireAlarm(dispatch)
    }
  })

  const handleDismissFireAlarm = () => {
    submitDismissAlarm()
  }

  useEffect(() => {
    if (isShowFireAlarmDialog && !isFireAlarmActive) {
      setIsShowFireAlarmDialog(false)
    }
  }, [isShowFireAlarmDialog, isFireAlarmActive]);

  useEffect(() => {
    if (getMapLevels().length <= 0) {
      return
    }

    const listLevels = getMapLevels();
    const isCurrentLevelExist = listLevels.find((level) => level.level.toString() === currentFloor.toString());

    if (!isCurrentLevelExist) {
      setTimeout(() => {
        dispatch(setCurrentFloor(listLevels[0].level.toString()));
      }, 500);
    }

  }, [currentFloor, getMapLevels().length]);

  const mapResize = () => {
    setTimeout(() => {
      map.current?.resize()
    }, 500);
  }

  useEffect(() => {
    window.addEventListener('resize', mapResize)

    return () => {
      window.removeEventListener('resize', mapResize)
    }
  }, [])

  useEffect(() => {
    setTimeout(() => {
      mapResize()
    }, 500);
  }, [isHideCard, mapHeight])

  return (
    <>
      <Box
        className="mapContainer"
        ref={mapContainer}
        sx={{
          width: "100%",
          height: "100%",
          // zIndex: 0,
        }}
      >
      </Box>
      <Stack
        sx={(theme) => ({
          [theme.breakpoints.up(VIEW.PHONE)]: {
            right: "15px",
            bottom: showExtendMenu ? "260px" : "100px",
          },
          [theme.breakpoints.up(VIEW.DESKTOP)]: {
            right: "40px",
            bottom: showExtendMenu ? "260px" : "120px",
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: "absolute",
          zIndex: 1000,
          tabindex: -1,
          bgcolor: "rgba(187, 187, 187, 0.5)",
          borderRadius: "31px",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          userSelect: 'none',
          ...smoothTransition,
        })}>
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", width: "62px", bgColor: 'red', margin: "10px auto" }}>
          {getMapLevels()?.map((level, index) => (
            <Typography
              key={index}
              sx={{
                fontSize: "18px",
                fontWeight: "600",
                color: currentFloor == level.level.toString() ? '#378FFE' : '#9e9e9e',
                textAlign: "center",
                mt: 1,
                mb: 1,
                '&:hover': {
                  cursor: "pointer",
                  color: "#378FFE"
                }
              }}
              onClick={() => dispatch(setCurrentFloor(level.level.toString()))}
            >
              {level.name}
            </Typography>
          ))}
        </Box>
      </Stack>
      {/* {selectedMapName !== '' && (
        <Stack style={{
          position: "absolute",
          bottom: '30px',
          right: '120px',
          height: '62px',
          display: "flex",
          gap: '8px',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'row-reverse',
        }}>
          <Stack>
            <Fab onClick={() => setShowControlMenu(!showControlMenu)} color="primary" aria-label="add">
              <NavigateBefore
                style={{
                  transform: showControlMenu ? "rotate(180deg)" : "",
                  transitionDuration: '300ms',
                  transitionProperty: 'all',
                  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              />
            </Fab>
          </Stack>
          <Stack style={{
            height: '100%',
            backgroundColor: '#FFF',
            color: '#000',
            display: showControlMenu ? "flex" : "flex",
            opacity: showControlMenu ? 100 : 0,
            transitionDuration: '300ms',
            transitionProperty: 'all',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            transform: showControlMenu ? "transform: translateX(0)" : "transform: translateX(0.625rem)",
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: '12px',
            paddingRight: '12px',
            paddingBottom: '6px',
            paddingTop: '6px',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
          }}>
            <Typography fontWeight={600} style={{ marginRight: "4px" }}>
              {selectedMapName}
            </Typography>
            <Stack direction={'row'}>
              {
                selectedMapName && loadedMaps.length > 0 && (
                  <>
                    <Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
                      <IconButton
                        onClick={() => onChangeOpacity(selectedOpacity - 1)}
                        onMouseDown={decreaseOpacityHandler}
                        onMouseUp={removeOpacityHandler}
                        onMouseLeave={removeOpacityHandler}
                      >
                        <VisibilityOffRounded sx={{ opacity: 0.3 }} />
                      </IconButton>
                      <Slider
                        min={0}
                        max={100}
                        value={selectedOpacity}
                        onChange={(_, value) => onChangeOpacity(value as number)}
                        sx={{ minWidth: '64px' }}
                      />
                      <IconButton
                        onMouseDown={increaseOpacityHandler}
                        onMouseUp={removeOpacityHandler}
                        onMouseLeave={removeOpacityHandler}
                        onClick={() => onChangeOpacity(selectedOpacity + 1)}
                      >
                        <VisibilityRounded />
                      </IconButton>
                    </Stack>
                  </>
                )
              }
              <Button
                onMouseDown={rotateLeftHandler}
                onMouseUp={removeRotateLeftHandler}
                onMouseLeave={removeRotateLeftHandler}
                onClick={onRotateLeft}
                sx={moreControlButtonStyle}
              >
                <RotateLeft />
              </Button>
              <Button
                onMouseDown={rotateRightHandler}
                onMouseUp={removeRotateRightHandler}
                onMouseLeave={removeRotateRightHandler}
                onClick={onRotateRight}
                sx={moreControlButtonStyle}
              >
                <RotateRight />
              </Button>
              <Button
                onClick={() => onChangeScale(0.9)}
                sx={moreControlButtonStyle}
              >
                <SouthWestRounded />
              </Button>
              <Button
                onClick={() => onChangeScale(1.1)}
                sx={moreControlButtonStyle}
              >
                <NorthEastRounded />
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )} */}
      <Stack sx={(theme) => ({
        [theme.breakpoints.up(VIEW.PHONE)]: {
          right: '95px',
          bottom: showExtendMenu ? '170px' : '25px',
        },
        [theme.breakpoints.up(VIEW.DESKTOP)]: {
          right: '120px',
          bottom: showExtendMenu ? '170px' : '25px',
        },
        zIndex: 1000,
        tabindex: -1,
        position: "absolute",
        height: '62px',
        display: "flex",
        gap: '8px',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row-reverse',
        ...smoothTransition
      })}>
        <Stack>
          <Button
            variant="contained"
            sx={{
              width: "62px",
              height: "62px",
              bgcolor: "#E0E0E0",
              color: "#202020",
              fontWeight: "600",
              borderRadius: "31px",
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              '&:hover': {
                cursor: "pointer",
                bgcolor: "#E5E5E5"
              }
            }}
            onClick={() => setShowExtendMenu(!showExtendMenu)}
          >
            <KeyboardArrowUp
              style={{
                transform: showExtendMenu ? "rotate(180deg)" : "",
                ...smoothTransition
              }}
            />
          </Button>
        </Stack>
      </Stack>

      {show3DFeature && (
        <Stack
          sx={(theme) => ({
            [theme.breakpoints.up(VIEW.PHONE)]: {
              right: "175px",
              bottom: showExtendMenu ? '170px' : '25px',
            },
            [theme.breakpoints.up(VIEW.DESKTOP)]: {
              right: "200px",
              bottom: showExtendMenu ? '170px' : '25px',
            },
            zIndex: 1000,
            height: '62px',
            display: "flex",
            alignItems: 'center',
            justifyContent: 'center',
            position: "absolute",
            color: "#000",
            backgroundColor: "#FFF",
            paddingInline: '16px',
            borderRadius: '0.75rem',
            ...smoothTransition
          })}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography>2D</Typography>
            <IOSSwitch sx={{ m: 1 }} checked={iconType === "3D"} onChange={(_, checked) => onIconTypeChange(checked ? "3D" : "2D")} />
            <Typography>3D</Typography>
          </Stack>
        </Stack>
      )}
      <Stack
        sx={(theme) => ({
          [theme.breakpoints.up(VIEW.PHONE)]: {
            right: "15px",
            bottom: showExtendMenu ? '170px' : '25px',
          },
          [theme.breakpoints.up(VIEW.DESKTOP)]: {
            right: "40px",
            bottom: showExtendMenu ? '170px' : '25px',
          },
          zIndex: 1000,
          position: "absolute",
          ...smoothTransition
        })}>
        {
          !triggerFireState && (
            <Button variant="contained" sx={{ width: "62px", height: "62px", bgcolor: "#FF0B0B", color: "white", fontWeight: "600", borderRadius: "31px", boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", '&:hover': { cursor: "pointer", bgcolor: "#bf0404" } }} onClick={() => handleFireAlarm()}>
              {/* <img src={parcel}style={{ width: "30px", height: "30px" }} /> */}
              <LocalFireDepartmentIcon sx={{ fontSize: "35px" }} />
            </Button>
          )
        }
      </Stack>

      <Stack sx={(theme) => ({
        [theme.breakpoints.up(VIEW.PHONE)]: {
          width: '100%',
        },
        [theme.breakpoints.up(VIEW.TABLET)]: {
          width: '360px',
          borderTopLeftRadius: '16px',
        },
        zIndex: 1001,
        tabindex: -1,
        position: 'absolute',
        bottom: '0',
        right: '0',
        height: showExtendMenu ? '160px' : '0px',
        overflow: 'hidden',
        backgroundColor: 'white',
        ...border.light,
        ...shadow,
        ...smoothTransition
      })}
      >
        <Box
          sx={{
            padding: '16px',
          }}
        >
          <div className="flex flex-col">
            <div
              className="flex items-center"
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '4px',
              }}
            >
              <span className="font-poppins text-dark text-md" style={{ fontWeight: 600 }}>Legend </span>
              <CloseIcon
                onClick={() => setShowExtendMenu(false)}
                sx={{
                  color: "#5A5A5A",
                  cursor: 'pointer',
                  fontSize: "14px"
                }}
              />
            </div>
            <div className="flex flex-col" style={{ gap: '2.5px', width: '100%' }}>
              <div className="flex items-center" style={{ width: '100%' }}>
                <span className="text-dark text-sm font-poppins" style={{ flex: 'auto' }}>Show all points</span>
                <Switch checked={isShowAll} onCheckedChange={(checked) => setShowAll(checked)} />
              </div>
              <div className="flex items-center">
                <PinPointIcon style={{
                  fontSize: '18px',
                  margin: '4px',
                  marginRight: '8px'
                }} />
                <span className="text-dark text-base font-poppins" style={{ flex: 'auto', marginBottom: '1px' }}>Delivery</span>
                <Switch checked={showDeliveryPoint} onCheckedChange={(checked) => setShowDeliveryPoint(checked)} />
              </div>
              <div className="flex items-center">
                <LiftIcon style={{
                  fontSize: '18px',
                  margin: '4px',
                  marginRight: '8px'
                }} />
                <span className="text-dark text-base font-poppins" style={{ flex: 'auto', marginBottom: '1px' }}>Lift</span>
                <Switch checked={showLift} onCheckedChange={(checked) => setShowLift(checked)} />
              </div>
              <div className="flex items-center">
                <DoorIcon style={{
                  fontSize: '18px',
                  margin: '4px',
                  marginRight: '8px'
                }} />
                <span className="text-dark text-base font-poppins" style={{ flex: 'auto' }}>Door</span>
                <Switch checked={showDoor} onCheckedChange={(checked) => setShowDoor(checked)} />
              </div>
            </div>
          </div>
        </Box>
      </Stack>

      <Dialog
        open={isShowFireAlarmDialog}
        maxWidth={false}
        BackdropProps={{ className: 'blink-backdrop' }}
        sx={(theme) => ({
          '& .MuiDialog-paper': {
            [theme.breakpoints.up(VIEW.PHONE)]: {
              left: '5px',
              right: '5px',
              mx: 'auto',
              width: '90vw',
              maxWidth: 'none'
            },
            [theme.breakpoints.up(VIEW.TABLET)]: {
              width: '600px',
            },
            [theme.breakpoints.up(VIEW.DESKTOP)]: {
              left: '50%',  // Center horizontally
              right: '50%',
              transform: 'translate(-50%, -10%)',
              height: '250px',
              width: '600px',
            },
            bgcolor: 'transparent',
            boxShadow: 'none',
            position: 'absolute',  // Use absolute positioning
            top: '10%',  // Adjust this value to control the vertical position
          }
        })}
      >
        <Box style={{
          border: `${criticalColor.border}`,
          borderRadius: "15px",
          backgroundColor: "#ffffff",
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            margin: 'auto',
            bgcolor: "#fff",
            p: 2,
            px: '30px'
          }}>
            <IconButton
              // onClick={() => { dispatch(setNotificationOff()) }}
              onClick={() => setIsShowFireAlarmDialog(false)}
              // if fire alarm prompt user to confirm before disabling
              // onClick={() => { notificationType === "fire" ? dispatch(setFireAlarmOff()) : dispatch(setNotificationOff()) }}
              sx={{
                position: 'absolute',
                right: 20,
                top: 20,
                color: "#727272"
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={(theme) => ({
              [theme.breakpoints.down(VIEW.TABLET)]: {
                flex: 'auto',
                width: '100%'
              },
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              height: '160px'
            })}>
              <Box sx={(theme) => ({
                [theme.breakpoints.up(VIEW.PHONE)]: {
                  height: "80px",
                  display: 'none'
                },
                [theme.breakpoints.up(VIEW.TABLET)]: {
                  height: "120px",
                  display: 'inline-block',
                  maxWidth: '200px',
                  flex: 'none'
                },
              })}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <FireAlarmIcon />
                </Box>
              </Box>
              <DialogContent sx={(theme) => ({
                [theme.breakpoints.up(VIEW.TABLET)]: {
                  ml: 3,
                },
                textAlign: "left",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#000",
                display: "flex",
                justifyContent: "center",
                alignItems: "left",
                flexDirection: "column",
                padding: 0
              })}>
                {/* Order has been delivered */}
                <Typography fontSize={'18px'} fontWeight={700}>
                  {fireNotificationTemplate.notification_details.title}
                </Typography>
                <Typography variant="h5" component="div" sx={{
                  color: "#727272",
                  fontSize: "16px",
                  textAlign: "left",
                  mt: 1.5
                }}>
                  {/* Please attend to the robot and receive the item at the TR+43 */}
                  {fireNotificationTemplate.notification_details.description}
                </Typography>
                <Box>
                  <Button variant="contained" sx={{
                    // position: 'absolute',
                    // bottom: 70,
                    // left: 185,
                    width: '100%',
                    mt: '12px',
                    color: "#fff",
                    backgroundColor: "#eb4034",
                    '&:hover': {
                      backgroundColor: "#b82218"
                    }
                  }}
                    onClick={handleDismissFireAlarm}
                  >
                    Disable Fire Alarm
                  </Button>
                </Box>
              </DialogContent>
            </Box>
            <Box sx={{}}>
              <Typography variant="h6" component="div" sx={{
                flexGrow: 1,
                color: "#727272",
                fontSize: "16px",
                textAlign: "right",
                mt: -2
              }}>
                {formatDate(Date.now() / 1000)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default RobotMap;
